// ROI Reporting — translates workforce assumptions into a defensible, cited
// business-outcome ROI (absence, presenteeism, productivity, injury, turnover,
// healthcare). Deliberately avoids pricing physiological metrics directly.
//
// Central-admin gated (see router.tsx / AdminSideNav centralOnly). Pure
// client-side calculator in ../data/model.ts — no backend calls.

import React, { useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import { DEFAULT_INPUTS, computeRoi, type RoiInputs } from "../data/model";
import { AssumptionsSection } from "../sections/AssumptionsSection";
import { SavingsSection } from "../sections/SavingsSection";
import { MethodologySection } from "../sections/MethodologySection";

type SectionKey = "assumptions" | "savings" | "methodology";

const NAV: { key: SectionKey; label: string; icon: React.ReactElement }[] = [
  { key: "assumptions", label: "Assumptions", icon: <TuneRoundedIcon fontSize="small" /> },
  { key: "savings", label: "Projected Savings", icon: <SavingsRoundedIcon fontSize="small" /> },
  { key: "methodology", label: "Methodology & Sources", icon: <MenuBookRoundedIcon fontSize="small" /> },
];

export const RoiReporting: React.FC = () => {
  const theme = useTheme();
  const [section, setSection] = useState<SectionKey>("savings");
  const [inputs, setInputs] = useState<RoiInputs>(DEFAULT_INPUTS);

  const result = useMemo(() => computeRoi(inputs), [inputs]);
  const activeLabel = NAV.find((n) => n.key === section)?.label ?? "";

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link underline="hover" color="inherit" href="#" onClick={(e) => e.preventDefault()}>
          Dashboards
        </Link>
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setSection("savings");
          }}
        >
          ROI Reporting
        </Link>
        <Typography color="text.primary">{activeLabel}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "232px 1fr" }, gap: 3 }}>
        {/* Left sub-navigation */}
        <Box component="nav" aria-label="ROI Reporting sections">
          <List
            sx={{
              position: { md: "sticky" },
              top: 16,
              borderRadius: 2,
              p: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            {NAV.map((item) => {
              const active = item.key === section;
              return (
                <ListItemButton
                  key={item.key}
                  selected={active}
                  onClick={() => setSection(item.key)}
                  aria-current={active ? "page" : undefined}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    "&.Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.16),
                      "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.22) },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: active ? "primary.main" : "text.secondary" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: active ? 800 : 500,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        {/* Section content */}
        <Box sx={{ minWidth: 0 }}>
          {section === "assumptions" && (
            <AssumptionsSection
              inputs={inputs}
              onChange={setInputs}
              onReset={() => setInputs(DEFAULT_INPUTS)}
            />
          )}
          {section === "savings" && (
            <SavingsSection
              result={result}
              attribution={inputs.attribution}
              onEditAssumptions={() => setSection("assumptions")}
            />
          )}
          {section === "methodology" && <MethodologySection />}
        </Box>
      </Box>
    </Box>
  );
};
